import { Company } from '@/api/model/company'
import { Branch } from '@/api/model/branch'
import { ScheduledDashboardExport } from '@/api/model/scheduled-dashboard-export'
import { BaseModel } from '@/api/base-model'
import { UserAPI } from '@/api/backend-client'
import {
    availableGenders,
    BranchType,
    DashboardView,
    Gender,
    Role,
    ScheduledDashboardExportSchedule,
    UserType,
} from '@/api/types'
import { v4 as uuid } from 'uuid'
import { GamificationBadge } from '@/api/model/gamification-badge'
import { GamificationScore } from '@/api/model/gamification-score'
import { Dashboard } from '@/api/model/dashboard'

export class UserDashboardPermission extends BaseModel<UserDashboardPermission> {
    constructor(
        public id: string,
        public permission: boolean|null,
        public dashboard: Dashboard,
    ) { super(id)}

    static fromData(data: any): UserDashboardPermission {
        if (data === null || typeof data === 'string' || data instanceof User) {
            return data
        }

        return new UserDashboardPermission(
            data.id,
            data.permission,
            data.dashboard ? Dashboard.fromData(data.dashboard) : null,
        )
    }
}

export class User extends BaseModel<User> {
    protected collectionName = UserAPI.collectionName

    constructor(
        public company: Company|null,
        public id: string,
        public type: UserType,
        public firstname: string|null,
        public middlename: string|null,
        public lastname: string|null,
        public avatar: any,
        public avatarUrl: any,
        public email: string,
        public password: string|null,
        public role: Role,
        public birthdate: string|null,
        public gender: Gender,
        public phoneNumber: string|null,
        public mobilePhoneNumber: string|null,
        public absent: boolean,
        public active: boolean,
        public branches: Branch[],
        public gamificationBadges: GamificationBadge[],
        public gamificationScores: GamificationScore[],
        public scheduledDashboardExports: ScheduledDashboardExport[],
        public realworksAccountManager: number|null,
        public defaultDashboardView: DashboardView,
        public dashboardPermissions: Array<{ dashboard: string, permission: boolean|null, powerBiCustomRole: string|null }>,
        public superAdmin: boolean,
        public betaOptInEnabled: boolean,
        public autoDailyMailEnabled: boolean,
        public autoAppointmentCreatedMailEnabled: boolean,
        public createdAt: any,
    ) { super(id) }

    getAvailableBranches(options: { type?: BranchType } = {}): Branch[] {
        return this.branches
            .filter(branch => branch.type === (options.type || BranchType.DEFAULT))
            .sort(((a, b) => a.name.localeCompare(b.name)))
    }

    hasRole(role: Role): boolean {
        switch (role) {
            case Role.EMPLOYEE: return this.isEmployee
            case Role.TEAM_LEAD: return this.isTeamLead
            case Role.BRANCH_MANAGER: return this.isBranchManager
            case Role.COMPANY_MANAGER: return this.isCompanyManager
        }

        return false
    }

    get name(): string {
        return [this.firstname, this.middlename, this.lastname]
            .filter(value => value !== null && value !== '')
            .join(' ')
    }

    get isEmployee(): boolean {
        if (this.role === Role.EMPLOYEE) { return true }
        if (this.role === Role.TEAM_LEAD) { return true }
        if (this.role === Role.BRANCH_MANAGER) { return true }
        if (this.role === Role.COMPANY_MANAGER) { return true }
        return false
    }

    get isTeamLead(): boolean {
        if (this.role === Role.TEAM_LEAD) { return true }
        if (this.role === Role.BRANCH_MANAGER) { return true }
        if (this.role === Role.COMPANY_MANAGER) { return true }
        return false
    }

    get isBranchManager(): boolean {
        if (this.role === Role.BRANCH_MANAGER) { return true }
        if (this.role === Role.COMPANY_MANAGER) { return true }
        return false
    }

    get isCompanyManager(): boolean {
        if (this.role === Role.COMPANY_MANAGER) { return true }
        return false
    }

    get canViewReports(): boolean {
        if (this.isBranchManager) {
            return true
        }

        if (this.dashboardPermissions && this.dashboardPermissions.some(v => v.permission === true)) {
            return true
        }

        return false
    }

    hasScheduledDashboardExport(dashboardId: string): boolean {
        return this.scheduledDashboardExports
            .filter(scheduledDashboardExport => scheduledDashboardExport instanceof ScheduledDashboardExport)
            .some((scheduledDashboardExport: ScheduledDashboardExport) => scheduledDashboardExport.dashboardId === dashboardId)
    }

    getScheduledDashboardExport(dashboardId: string): ScheduledDashboardExport|null {
        return this.scheduledDashboardExports
            .filter(scheduledDashboardExport => scheduledDashboardExport instanceof ScheduledDashboardExport)
            .find((scheduledDashboardExport: ScheduledDashboardExport) => scheduledDashboardExport.dashboardId === dashboardId)
    }

    addScheduledDashboardExport(dashboardId: string, schedule: ScheduledDashboardExportSchedule): ScheduledDashboardExport {
        const scheduledDashboardExport = new ScheduledDashboardExport(
            this,
            uuid(),
            dashboardId,
            schedule,
            new Date(),
            new Date(),
        )

        this.scheduledDashboardExports.push(scheduledDashboardExport)

        return scheduledDashboardExport
    }

    removeScheduledDashboardExport(dashboardId: string): ScheduledDashboardExport|null {
        const scheduledDashboardExport = this.getScheduledDashboardExport(dashboardId)

        this.scheduledDashboardExports = this.scheduledDashboardExports
            .filter(existingScheduledDashboardExport => existingScheduledDashboardExport !== scheduledDashboardExport)

        return scheduledDashboardExport
    }

    static get validGenders(): string[] {
        return availableGenders()
    }

    static fromData(data: any): User {
        if (data === null || typeof data === 'string' || data instanceof User) {
            return data
        }

        const user = new User(
            data.company ? Company.fromData(data.company) : null,
            data.id,
            data.type,
            data.firstname,
            data.middlename,
            data.lastname,
            data.avatar,
            data.avatarUrl,
            data.email,
            data.password,
            data.role,
            data.birthdate,
            data.gender,
            data.phoneNumber,
            data.mobilePhoneNumber,
            data.absent,
            data.active,
            Object.values(data.branches || {}).map(entry => Branch.fromData(entry)),
            Object.values(data.gamificationBadges || {}).map(entry => GamificationBadge.fromData(entry)),
            Object.values(data.gamificationScores || {}).map(entry => GamificationScore.fromData(entry)),
            Object.values(data.scheduledDashboardExports || {}).map(entry => ScheduledDashboardExport.fromData(entry)),
            data.realworksAccountManagerId,
            data.defaultDashboardView,
            data.dashboardPermissions,
            data.superAdmin,
            data.betaOptInEnabled,
            data.autoDailyMailEnabled,
            data.autoAppointmentCreatedMailEnabled,
            data.createdAt,
        )

        user.branches.forEach((branch: Branch) => branch.company = branch.company || user.company)

        return user
    }
}
